import type { TokenItem } from '@/typings/token'
import { TaskType } from '@/constants/image'
import { defineStore } from 'pinia'
import { ref } from 'vue'

interface TaskCostCacheItem {
  credits: number
  timestamp: number
}

interface TaskCostCache {
  [key: string]: TaskCostCacheItem
}

export const useTokenStore = defineStore('token', () => {
  const creditList = ref<TokenItem[]>([])
  const taskCostCache = ref<TaskCostCache>({})
  
  const setCreditList = (list: TokenItem[]) => {
    creditList.value = list
  }

  /**
   * 获取任务成本缓存
   * @param taskType 任务类型
   * @param scene 场景信息
   * @returns 缓存的成本信息或null
   */
  const getCachedTaskCost = (taskType: TaskType, scene: { type: string; cost_level: number }): number | null => {
    try {
      const cacheKey = `${taskType}_${scene.type}_${scene.cost_level}`
      const cachedData = taskCostCache.value[cacheKey]

      if (!cachedData) return null

      // 检查缓存是否过期 (24小时)
      const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000
      const now = Date.now()
      if (now - cachedData.timestamp > CACHE_EXPIRY_TIME) {
        // 删除过期的缓存项
        delete taskCostCache.value[cacheKey]
        return null
      }

      return cachedData.credits
    } catch (error) {
      console.error('Failed to get cached task cost:', error)
      return null
    }
  }

  /**
   * 设置任务成本缓存
   * @param taskType 任务类型
   * @param scene 场景信息
   * @param credits 点数消耗
   */
  const setCachedTaskCost = (taskType: TaskType, scene: { type: string; cost_level: number }, credits: number): void => {
    try {
      const cacheKey = `${taskType}_${scene.type}_${scene.cost_level}`
      taskCostCache.value[cacheKey] = {
        credits,
        timestamp: Date.now()
      }
    } catch (error) {
      console.error('Failed to set cached task cost:', error)
    }
  }

  /**
   * 清除所有任务成本缓存
   */
  const clearTaskCostCache = (): void => {
    try {
      taskCostCache.value = {}
      console.info('Task cost cache cleared')
    } catch (error) {
      console.error('Failed to clear task cost cache:', error)
    }
  }

  /**
   * 清除过期的任务成本缓存
   */
  const clearExpiredTaskCostCache = (): void => {
    try {
      const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000
      const now = Date.now()
      let hasChanges = false

      Object.keys(taskCostCache.value).forEach(key => {
        if (now - taskCostCache.value[key].timestamp > CACHE_EXPIRY_TIME) {
          delete taskCostCache.value[key]
          hasChanges = true
        }
      })

      if (hasChanges) {
        console.info('Expired task cost cache items cleared')
      }
    } catch (error) {
      console.error('Failed to clear expired task cost cache:', error)
    }
  }

  return {
    creditList,
    taskCostCache,
    setCreditList,
    getCachedTaskCost,
    setCachedTaskCost,
    clearTaskCostCache,
    clearExpiredTaskCostCache
  }
})
